import { Avatar, Badge, Dropdown, Menu, Space, Typography, Flex } from 'antd';
import Icon, {
  UserOutlined,
  FileTextOutlined,
  KeyOutlined,
  LogoutOutlined,
  CodeOutlined,
  CodeFilled,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Logo, Container, Notification } from './styles';
import { selectLang, setLang } from '../../slices/lang';
import { useDispatch, useSelector } from 'react-redux';
import { Lang } from '../../types';
import { useTheme } from 'styled-components';
import HeaderMenu from './menu';
import { useLocation, useNavigate } from 'react-router';
import { SVGLogoLight, SVGBell } from '../../images';
import {
  selectIsAdmin,
  selectIsSuperAdmin,
  selectRoles,
  selectUser,
} from '../../slices/profile';
import { selectNotificationsNumber } from '../../slices/notifications';
import NotificationDrawer from '../../features/notification/notificationDrawer';
import DownloadLogModal from '../../components/downloadLogModal';
import { useState } from 'react';

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const lang = useSelector(selectLang);
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const roles = useSelector(selectRoles);
  const isAdmin = useSelector(selectIsAdmin);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const notificationsNumber = useSelector(selectNotificationsNumber);
  const user = useSelector(selectUser);

  const [showNotification, setShowNotification] = useState(false);
  const [showDownloadLogModal, setShowDownloadLogModal] = useState(false);

  const showHeaderMenu = pathname !== '/';

  const handleLangChange = () => {
    const nextLang = lang === Lang.en ? Lang.de : Lang.en;
    i18n.changeLanguage(nextLang);
    dispatch(setLang(nextLang));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // 构建下拉菜单项，分为两个 section
  const userMenuItems = [
    // 第一 section：用户信息和许可证（灰色背景卡片）
    {
      type: 'group',
      label: (
        <div
          style={{
            padding: '16px',
            background: '#f5f5f5',
            borderRadius: '8px',
            margin: '4px',
            minWidth: '280px',
          }}
        >
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {/* 用户名和头像行 */}
            <Space>
              <Avatar
                size={44}
                style={{ backgroundColor: theme.antd.colorPrimary }}
                icon={<UserOutlined />}
              />
              <Space direction="vertical" size={0}>
                <Typography.Text strong>
                  {[user.firstName, user.lastName].filter(Boolean).join(' ') ||
                    user.username}
                </Typography.Text>
                {user.companyName && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {user.companyName}
                  </Typography.Text>
                )}

                {user.email && (
                  <Typography.Text style={{ fontSize: 12 }}>
                    {user.email}
                  </Typography.Text>
                )}
              </Space>
            </Space>

            {user.licenseType && (
              <Flex gap={8} align="center" style={{ marginTop: 4 }}>
                <FileTextOutlined style={{ fontSize: 12, color: '#666' }} />
                <Typography.Text style={{ fontSize: 12 }}>
                  {user.licenseType === 'PRODUCTION'
                    ? 'Production License'
                    : user.licenseType === 'TRIAL'
                      ? 'Trial License'
                      : user.licenseType}
                </Typography.Text>
                {user.licenseRemainingDays !== undefined && (
                  <Typography.Text
                    type={
                      user.licenseRemainingDays <= 7
                        ? 'danger'
                        : user.licenseRemainingDays <= 30
                          ? 'warning'
                          : 'secondary'
                    }
                    style={{ fontSize: 12 }}
                  >
                    ({user.licenseRemainingDays} days left)
                  </Typography.Text>
                )}
              </Flex>
            )}
          </Space>
        </div>
      ),
    } as any,
    {
      type: 'divider',
    } as any,
    {
      key: 'changePassword',
      icon: <KeyOutlined style={{ fontSize: 14 }} />,
      label: 'Change Password',
      onClick: () => navigate('/change-password'),
      style: { padding: '10px 16px', margin: '0 12px' },
    },
    {
      key: 'logout',
      icon: <LogoutOutlined style={{ fontSize: 14 }} />,
      label: 'Log Out',
      onClick: handleLogout,
      style: { padding: '10px 16px', margin: '0 12px' },
    },
  ];

  return (
    <Container gap={16} align="center">
      <Logo onClick={() => navigate('/')} align="center" justify="center">
        <SVGLogoLight />
      </Logo>
      <nav style={{ flex: 1 }}>{showHeaderMenu && <HeaderMenu />}</nav>
      {/* <Switch
        checkedChildren={lang}
        unCheckedChildren={lang === Lang.en ? Lang.de : Lang.en}
        onChange={handleLangChange}
      /> */}
      {(isSuperAdmin || isAdmin) && (
        <Menu
          items={[
            {
              key: 'userManagement',
              label: 'User Management',
              onClick: () => navigate('/user'),
            },
          ]}
        ></Menu>
      )}
      {isSuperAdmin && (
        <Menu
          items={[
            {
              key: 'clientManagement',
              label: 'Client Management',
              onClick: () => navigate('/client'),
            },
          ]}
        ></Menu>
      )}
      {isSuperAdmin && (
        <Notification onClick={() => setShowDownloadLogModal(true)}>
          <CodeFilled style={{ color: '#F7F7F7', fontSize: 20 }} />
        </Notification>
      )}
      <Notification onClick={() => setShowNotification(true)}>
        <Badge count={notificationsNumber} size="small">
          <SVGBell fill="#F7F7F7" />
        </Badge>
      </Notification>
      <Dropdown
        menu={{ items: userMenuItems }}
        placement="bottomRight"
        arrow={false}
        trigger={['click']}
        getPopupContainer={(trigger) => document.body}
        dropdownRender={(menu) => (
          <div
            style={{
              minWidth: '300px',
              padding: '4px',
            }}
          >
            {menu}
          </div>
        )}
      >
        <Space
          style={{
            cursor: 'pointer',
            borderRadius: 4,
          }}
          onClick={(e) => e.preventDefault()}
        >
          <Avatar
            style={{ backgroundColor: theme.antd.colorPrimary }}
            icon={<UserOutlined />}
          />
        </Space>
      </Dropdown>
      <NotificationDrawer
        open={showNotification}
        onClose={() => setShowNotification(false)}
      />
      <DownloadLogModal
        open={showDownloadLogModal}
        onClose={() => setShowDownloadLogModal(false)}
      />
    </Container>
  );
};

export default Header;
